import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageShell } from '../components/PageShell.tsx';
import { skeletonKeys } from '../lib/skeletonKeys.ts';
import { useImportBlock, useBlocks } from '../hooks/useBlocks.ts';
import { useNoRides } from '../hooks/useSetup.ts';
import { NoRidesCallout } from '../components/setup/NoRidesCallout.tsx';
import type { TrainingBlockSummary } from '../types/api.ts';
import {
  blockStatusClass,
  formatBlockStatus,
  formatDateRange,
} from '../lib/blockFormat.ts';

export function Blocks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useBlocks('all');
  const importMut = useImportBlock();
  const fileRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState('');
  const noRides = useNoRides();

  const blocks = data?.blocks ?? [];
  const active = blocks.find((b) => b.status === 'active') ?? null;
  const drafts = blocks.filter((b) => b.status === 'draft');
  const completed = blocks.filter(
    (b) => b.status === 'completed' || b.status === 'abandoned',
  );

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setImportError('');
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const result = await importMut.mutateAsync({ payload });
      navigate(`/blocks/${result.id}`);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('blocks.list.importFailed');
      setImportError(msg);
    }
  };

  return (
    <PageShell
      title={t('blocks.list.title')}
      subtitle={t('blocks.list.subtitle')}
    >
      <div className="flex flex-col gap-6">
        {noRides && <NoRidesCallout />}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importMut.isPending}
            className="px-4 py-2 rounded-lg bg-(--color-accent)/15 text-(--color-accent) hover:bg-(--color-accent)/25 transition-colors text-sm font-medium border border-(--color-accent)/30 disabled:opacity-50"
          >
            {importMut.isPending
              ? t('blocks.list.importing')
              : `+ ${t('blocks.list.importJson')}`}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
          <a
            href="https://github.com/Mc231/cycling_coach/blob/main/docs/training-block-protocol.md"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-(--color-text-secondary) hover:text-(--color-text-primary) underline"
          >
            {t('blocks.list.protocolDocs')} {'↗'}
          </a>
        </div>

        {importError && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 text-sm">
            {importError}
          </div>
        )}

        {isLoading && <BlocksSkeleton />}

        {isError && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 text-sm flex items-center justify-between">
            <span>{t('blocks.list.errorLoading')}</span>
            <button
              onClick={() => refetch()}
              className="underline text-red-300 hover:text-red-200"
            >
              {t('blocks.list.retry')}
            </button>
          </div>
        )}

        {!isLoading && !isError && blocks.length === 0 && (
          <EmptyState onUpload={() => fileRef.current?.click()} />
        )}

        {active && (
          <Section title={t('blocks.list.activeTitle')}>
            <ActiveBlockCard block={active} />
          </Section>
        )}

        {drafts.length > 0 && (
          <Section title={t('blocks.list.draftsTitle')}>
            <ul className="flex flex-col gap-2">
              {drafts.map((b) => (
                <BlockListRow key={b.id} block={b} />
              ))}
            </ul>
          </Section>
        )}

        {completed.length > 0 && (
          <Section title={t('blocks.list.historyTitle')}>
            <ul className="flex flex-col gap-2">
              {completed.map((b) => (
                <BlockListRow key={b.id} block={b} />
              ))}
            </ul>
          </Section>
        )}
      </div>
    </PageShell>
  );
}

function Section({
  title,
  children,
}: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
        {title}
      </h2>
      {children}
    </section>
  );
}

function ActiveBlockCard({
  block,
}: Readonly<{ block: TrainingBlockSummary }>) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/blocks/${block.id}`)}
      className="w-full text-left rounded-xl bg-(--color-bg-secondary) border border-(--color-accent)/40 p-5 hover:border-(--color-accent) transition-colors flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-(--color-text-primary)">
            {block.name}
          </h3>
          <p className="text-sm text-(--color-text-secondary) mt-1">
            {formatDateRange(block.start_date, block.end_date)}
          </p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${blockStatusClass(block.status)}`}
        >
          {formatBlockStatus(block.status)}
        </span>
      </div>
      {block.description && (
        <p className="text-sm text-(--color-text-secondary)">
          {block.description}
        </p>
      )}
      <p className="text-xs text-(--color-text-secondary) mt-1">
        {block.duration_days} {block.duration_days === 1 ? 'day' : 'days'}
      </p>
    </button>
  );
}

function BlockListRow({ block }: Readonly<{ block: TrainingBlockSummary }>) {
  const navigate = useNavigate();
  return (
    <li>
      <button
        onClick={() => navigate(`/blocks/${block.id}`)}
        className="w-full text-left rounded-lg bg-(--color-bg-secondary) border border-(--color-border) hover:border-(--color-accent)/40 hover:bg-(--color-bg-tertiary)/40 transition-colors p-3 flex items-center justify-between gap-3"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-(--color-text-primary) truncate">
            {block.name}
          </div>
          <div className="text-xs text-(--color-text-secondary)">
            {formatDateRange(block.start_date, block.end_date)} ·{' '}
            {block.duration_days} {block.duration_days === 1 ? 'day' : 'days'}
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${blockStatusClass(block.status)}`}
        >
          {formatBlockStatus(block.status)}
        </span>
      </button>
    </li>
  );
}

function EmptyState({ onUpload }: Readonly<{ onUpload: () => void }>) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-8 text-center">
      <span className="text-5xl">{'\u{1F4DD}'}</span>
      <h2 className="text-lg font-semibold text-(--color-text-primary) mt-3">
        {t('blocks.list.emptyTitle')}
      </h2>
      <p className="text-(--color-text-secondary) mt-2 text-sm max-w-md mx-auto">
        {t('blocks.list.emptyBody')}
      </p>
      <button
        onClick={onUpload}
        className="mt-4 px-4 py-2 rounded-lg bg-(--color-accent)/15 text-(--color-accent) hover:bg-(--color-accent)/25 transition-colors text-sm font-medium border border-(--color-accent)/30"
      >
        + {t('blocks.list.importJson')}
      </button>
    </div>
  );
}

const BLOCKS_SKELETON_KEYS = skeletonKeys(3);

function BlocksSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      {BLOCKS_SKELETON_KEYS.map((key) => (
        <div
          key={key}
          className="h-20 rounded-xl bg-(--color-bg-secondary) border border-(--color-border)"
        />
      ))}
    </div>
  );
}
